/**
 * Custom laser engraving for the product page.
 *
 * The engraving is a separate add-on product. When the customer saves an engraving the inputs get
 * their `name` attributes (properties[Engraving Text] / properties[Engraving Logo] / properties[_uid]),
 * and the theme's product form calls window.ProductEngraving.buildPairedFormData() to submit the bottle
 * and the add-on together, linked by the shared _uid property.
 */
class EngravingBox extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    const configEl = document.querySelector('[data-engraving-config]');
    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    this.box = this.querySelector('.engraving__box');
    this.header = this.querySelector('.engraving__header');
    this.content = this.querySelector('.engraving__content');
    this.titleEl = this.querySelector('[data-engraving-title]');
    this.preview = this.querySelector('[data-engraving-preview]');
    this.actions = this.querySelector('[data-engraving-actions]');
    this.errorEl = this.querySelector('[data-engraving-error]');
    this.textInput = this.querySelector('[data-engraving-text]');
    this.fileInput = this.querySelector('[data-engraving-file]');
    this.fileName = this.querySelector('[data-engraving-file-name]');
    this.logoUrlInput = this.querySelector('[data-engraving-logo-url]');
    this.uidInput = this.querySelector('[data-engraving-uid]');
    this.defaultFileName = this.fileName.textContent;
    this.tabs = this.querySelectorAll('[data-engraving-tab]');
    this.panels = this.querySelectorAll('[data-engraving-panel]');

    this.header.addEventListener('click', () => this.toggle());
    this.querySelector('[data-engraving-edit]').addEventListener('click', () => this.toggle(true));
    this.querySelector('[data-engraving-remove]').addEventListener('click', () => this.clear());
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.showTab(tab.dataset.engravingTab)));

    this.querySelector('[data-engraving-save-text]').addEventListener('click', () => this.saveText());
    this.textInput.addEventListener('keydown', (evt) => {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.saveText();
      }
    });
    this.querySelector('[data-engraving-pick-file]').addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', () => this.uploadLogo());

    // Start fresh after the bottle has been added to the cart
    document.addEventListener('on:cart:add', () => {
      if (EngravingBox.active) this.clear();
    });
    // Browser back/forward cache can restore stale values
    window.addEventListener('pageshow', (evt) => {
      if (evt.persisted) this.clear();
    });

    EngravingBox.instance = this;
    this.updatePaymentButton();
  }

  get isSaved() {
    return this.uidInput.hasAttribute('name') && Boolean(this.uidInput.value);
  }

  static get active() {
    return Boolean(EngravingBox.instance && EngravingBox.instance.isSaved);
  }

  toggle(force) {
    const open = typeof force === 'boolean' ? force : this.content.hidden;
    this.content.hidden = !open;
    this.box.classList.toggle('is-open', open);
    this.header.setAttribute('aria-expanded', String(open));
    this.setError('');
  }

  showTab(mode) {
    this.tabs.forEach((tab) => {
      const on = tab.dataset.engravingTab === mode;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', String(on));
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.engravingPanel !== mode;
    });
    this.setError('');
  }

  setError(message) {
    this.errorEl.hidden = !message;
    this.errorEl.textContent = message || '';
  }

  setUid(on) {
    if (on) {
      this.uidInput.setAttribute('name', 'properties[_uid]');
      this.uidInput.value = String(Date.now());
    } else {
      this.uidInput.removeAttribute('name');
      this.uidInput.value = '';
    }
  }

  saveText() {
    const value = this.textInput.value.trim();
    if (!value) {
      this.setError(this.config.strings.textRequired);
      return;
    }
    this.logoUrlInput.removeAttribute('name');
    this.logoUrlInput.value = '';
    this.textInput.value = value;
    this.textInput.setAttribute('name', 'properties[Engraving Text]');
    this.setUid(true);
    this.markSaved(value);
  }

  async uploadLogo() {
    const file = this.fileInput.files && this.fileInput.files[0];
    if (!file) return;

    const { cloudName, uploadPreset, strings } = this.config;
    if (!cloudName || !uploadPreset) {
      this.setError(strings.uploadError);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.fileInput.value = '';
      this.setError(strings.uploadTooLarge);
      return;
    }

    this.setError(strings.uploading);
    this.classList.add('is-uploading');

    const body = new FormData();
    body.append('file', file);
    body.append('upload_preset', uploadPreset);

    try {
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, { method: 'POST', body });
      const data = await response.json();
      if (!response.ok || !data.secure_url) throw new Error(data.error ? data.error.message : 'Upload failed');

      this.textInput.removeAttribute('name');
      this.textInput.value = '';
      this.logoUrlInput.value = data.secure_url;
      this.logoUrlInput.setAttribute('name', 'properties[Engraving Logo]');
      this.setUid(true);
      this.fileName.textContent = file.name;
      this.markSaved(file.name);
      this.setError('');
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
      this.fileInput.value = '';
      this.setError(strings.uploadError);
    } finally {
      this.classList.remove('is-uploading');
    }
  }

  markSaved(previewText) {
    this.classList.add('is-saved');
    this.titleEl.textContent = this.config.strings.applied;
    this.preview.textContent = previewText;
    this.preview.hidden = false;
    this.actions.hidden = false;
    this.toggle(false);
    this.updatePaymentButton();
  }

  clear() {
    this.classList.remove('is-saved');
    this.titleEl.textContent = this.config.strings.add;
    this.preview.textContent = '';
    this.preview.hidden = true;
    this.actions.hidden = true;
    this.textInput.removeAttribute('name');
    this.textInput.value = '';
    this.logoUrlInput.removeAttribute('name');
    this.logoUrlInput.value = '';
    this.fileInput.value = '';
    this.fileName.textContent = this.defaultFileName;
    this.setUid(false);
    this.toggle(false);
    this.updatePaymentButton();
  }

  /**
   * Accelerated checkout buttons ("Buy it now") submit the bottle on its own and would drop the engraving.
   */
  updatePaymentButton() {
    const form = document.getElementById(this.dataset.formId);
    const container = form && form.querySelector('.product-dynamic-payment-button');
    if (container) container.hidden = this.isSaved;
  }
}

customElements.define('engraving-box', EngravingBox);

window.ProductEngraving = {
  isActive: () => EngravingBox.active,

  /**
   * Build FormData for /cart/add.js containing the bottle and the engraving add-on.
   * @param {HTMLFormElement} form - The product form that is being submitted.
   * @returns {FormData}
   */
  buildPairedFormData(form) {
    const box = EngravingBox.instance;
    const source = new FormData(form);
    const properties = {};
    for (const [key, value] of source.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    }

    // Fresh uid on every add so repeated adds of the same engraving stay separate lines
    const uid = String(Date.now());
    properties._uid = uid;
    box.uidInput.value = uid;

    const quantity = parseInt(source.get('quantity'), 10) || 1;
    const items = [
      { id: source.get('id'), quantity, properties },
      { id: box.config.variantId, quantity, properties: { _uid: uid } }
    ];

    const formData = new FormData();
    items.forEach((item, i) => {
      formData.append(`items[${i}][id]`, item.id);
      formData.append(`items[${i}][quantity]`, item.quantity);
      Object.entries(item.properties).forEach(([key, value]) => {
        formData.append(`items[${i}][properties][${key}]`, value);
      });
    });
    return formData;
  }
};
